/** 参数维度名：集成开关（与 fa_offer_parameter 根节点一致） */
export const SWITCH_PARAMETER_NAME = '集成开关';

export function isSwitchParameter(item) {
  return String((item && item.name) || '').trim() === SWITCH_PARAMETER_NAME;
}

export function isSkuSwitchEnabled(sku) {
  if (!sku) return false;
  return sku.switch_enable !== false && sku.switch_enable !== 0 && sku.switch_enable !== '0';
}

/** 当前规格组合是否展示「集成开关」参数行 */
export function shouldShowSwitchParameter(good, skuActive) {
  if (!good) return false;
  const isSku = good.is_sku === 1 || good.is_sku === true || good.is_sku === '1';
  if (!isSku) return true;
  if (skuActive < 0) return false;
  const sku = good.skuPrice && good.skuPrice[skuActive];
  return isSkuSwitchEnabled(sku);
}

export function shouldShowParameterItem(item, good, skuActive) {
  if (!isSwitchParameter(item)) return true;
  return shouldShowSwitchParameter(good, skuActive);
}

/** 提交/算价用的 parameter_ids（隐藏集成开关时不带上其选项） */
export function collectParameterIds(parameterList, good, skuActive) {
  const showSwitch = shouldShowSwitchParameter(good, skuActive);
  return (parameterList || [])
    .filter((item) => showSwitch || !isSwitchParameter(item))
    .map((item) => item.activeId)
    .filter((id) => id !== '' && id != null)
    .join(',');
}

/** 规格切换后清空已隐藏的集成开关选择 */
export function syncSwitchParameterVisibility(vm, good, skuActive) {
  if (!good || !Array.isArray(good.parameterList)) return;
  if (shouldShowSwitchParameter(good, skuActive)) return;
  good.parameterList.forEach((item, index) => {
    if (!isSwitchParameter(item)) return;
    if (!item.activeId && !item.activeMidId && (item.activeIndex == null || item.activeIndex < 0)) return;
    vm.$set(good.parameterList, index, normalizeParameterItem(item, ''));
  });
}

/** 解析购物车 parameter_ids（过滤空段，避免 "9,,112" 错位） */
export function parseParameterIds(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  return String(raw)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

/** 参数维度是否含中间层（如 12V/24V → 3000K） */
export function isNestedParameter(item) {
  const children = item && item.children;
  if (!Array.isArray(children) || !children.length) return false;
  return children.some((c) => Array.isArray(c.children) && c.children.length > 0);
}

export function leafOptionsForMid(item, midId) {
  if (!item || !midId) return [];
  const mid = (item.children || []).find((c) => Number(c.id) === Number(midId));
  return mid && Array.isArray(mid.children) ? mid.children : [];
}

export function findLeafInParameter(item, leafId) {
  if (!item || leafId === '' || leafId == null) return null;
  for (const mid of item.children || []) {
    if (Array.isArray(mid.children) && mid.children.length) {
      for (const leaf of mid.children) {
        if (Number(leaf.id) === Number(leafId)) {
          return { mid, leaf };
        }
      }
    } else if (Number(mid.id) === Number(leafId)) {
      return { mid: null, leaf: mid };
    }
  }
  return null;
}

/** 在该维度树中匹配已选叶子 id */
export function resolveSelectedLeafId(item, selectedLeafIds) {
  const ids = Array.isArray(selectedLeafIds) ? selectedLeafIds : parseParameterIds(selectedLeafIds);
  for (const id of ids) {
    if (findLeafInParameter(item, id)) return id;
  }
  return '';
}

/** 初始化/恢复参数选择状态（返回新对象，保证 Vue2 响应式） */
export function normalizeParameterItem(item, selectedLeafId) {
  if (!item) return item;

  const base = {
    ...item,
    activeMidId: '',
    activeMidLabel: '',
    activeId: '',
    activeLabel: '',
    activeIndex: -1,
  };

  if (isNestedParameter(item)) {
    const hit = selectedLeafId ? findLeafInParameter(item, selectedLeafId) : null;
    if (hit && hit.mid) {
      base.activeMidId = hit.mid.id;
      base.activeMidLabel = hit.mid.name;
      base.activeId = hit.leaf.id;
      base.activeLabel = hit.leaf.name;
      return base;
    }
    if (Number(item.must) === 1) {
      const firstMid = (item.children || []).find((c) => Array.isArray(c.children) && c.children.length);
      if (firstMid && firstMid.children.length) {
        base.activeMidId = firstMid.id;
        base.activeMidLabel = firstMid.name;
        base.activeId = firstMid.children[0].id;
        base.activeLabel = firstMid.children[0].name;
      }
    }
    return base;
  }

  const children = Array.isArray(item.children) ? [...item.children] : [];
  if (Number(item.must) === 1 && children.length) {
    base.activeLabel = children[0].name;
    base.activeId = children[0].id;
    base.activeIndex = 0;
  } else if (children.length) {
    base.children = [{ id: '', name: '请选择' }, ...children.filter((c) => c.id !== '')];
  } else {
    base.children = children;
  }

  if (selectedLeafId) {
    base.children.forEach((child, idx) => {
      if (Number(child.id) === Number(selectedLeafId)) {
        base.activeId = child.id;
        base.activeLabel = child.name;
        base.activeIndex = idx;
      }
    });
  }
  return base;
}

/** 按 parameter_ids 与维度树匹配后初始化整表参数 UI */
export function normalizeParameterList(list, parameterIdsRaw) {
  const selectedIds = parseParameterIds(parameterIdsRaw);
  return (list || []).map((item) => {
    const leafId = resolveSelectedLeafId(item, selectedIds);
    return normalizeParameterItem(item, leafId);
  });
}

export function onMidParameterChange(item, midId) {
  if (!item) return item;
  item.activeMidId = midId;
  const mid = (item.children || []).find((c) => Number(c.id) === Number(midId));
  item.activeMidLabel = mid ? mid.name : '';
  item.activeId = '';
  item.activeLabel = '';
  const leaves = leafOptionsForMid(item, midId);
  if (Number(item.must) === 1 && leaves.length) {
    item.activeId = leaves[0].id;
    item.activeLabel = leaves[0].name;
  }
  return item;
}

export function onLeafParameterChange(item, leafId) {
  if (!item) return item;
  item.activeId = leafId;
  const leaves = leafOptionsForMid(item, item.activeMidId);
  const leaf = leaves.find((c) => Number(c.id) === Number(leafId));
  item.activeLabel = leaf ? leaf.name : '';
  return item;
}

export function flatLeafParameterChange(item, leafId) {
  if (!item) return item;
  item.children.forEach((child, idx) => {
    if (Number(child.id) === Number(leafId)) {
      item.activeIndex = idx;
      item.activeId = child.id;
      item.activeLabel = child.name;
    }
  });
  return item;
}
